import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, IconButton, TextField, InputAdornment, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Tooltip, Typography,
} from '@mui/material';
import { Refresh, Search, WarningAmberRounded, Inventory2Outlined, PlaylistAddCheck, ShoppingCartCheckout } from '@mui/icons-material';
import inventoryCommandService from '../../services/inventoryCommandService';
import {
  PageHeader, StatCard, FilterBar, FilterSpacer, TableSurface, headerCellSx, bodyRowSx,
  StatusDot, ErrorState, TableEmptyState,
} from '../../components/common/pageKit';

// ---------------------------------------------------------------------------
// Types — mirrors inventoryCommandService.getReorderWorklist() on the API.
// ---------------------------------------------------------------------------

interface ReorderVendor {
  vendorId: number;
  vendorName: string;
}

interface ReorderRow {
  productId: number;
  productCode: string;
  productName: string;
  warehouseId?: number;
  warehouseName?: string;
  currentStock: number;
  minimumStock?: number;
  maximumStock?: number;
  reorderLevel: number;
  safetyStock: number;
  leadTimeDays: number;
  dailyConsumptionRate: number;
  targetLevel: number;
  recommendedOrderQty: number;
  preferredVendor: ReorderVendor | null;
  createPrLink: string | null;
}

const ReorderPlanningPage: React.FC = () => {
  const navigate = useNavigate();
  const [worklist, setWorklist] = useState<ReorderRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    setError(null);
    inventoryCommandService
      .reorder()
      .then((res) => setWorklist(Array.isArray(res?.worklist) ? (res.worklist as ReorderRow[]) : []))
      .catch((e: any) => setError(e?.message || 'Failed to load the reorder worklist'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = worklist || [];

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (r) =>
        (r.productName || '').toLowerCase().includes(term) ||
        (r.productCode || '').toLowerCase().includes(term) ||
        (r.warehouseName || '').toLowerCase().includes(term)
    );
  }, [rows, search]);

  const belowSafetyCount = useMemo(
    () => rows.filter((r) => r.safetyStock > 0 && r.currentStock <= r.safetyStock).length,
    [rows]
  );
  const totalRecommendedQty = useMemo(
    () => rows.reduce((sum, r) => sum + (Number(r.recommendedOrderQty) || 0), 0),
    [rows]
  );
  const vendorCount = useMemo(() => {
    const ids = new Set(rows.filter((r) => r.preferredVendor).map((r) => r.preferredVendor!.vendorId));
    return ids.size;
  }, [rows]);

  return (
    <Box>
      <PageHeader
        title="Reorder Planning"
        subtitle="Items at or below their reorder point, with a deterministic recommended order quantity"
        actions={
          <IconButton onClick={load} disabled={loading}>
            <Refresh />
          </IconButton>
        }
      />

      {loading && worklist === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <>
          <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard title="Items Below Reorder" value={rows.length} icon={<Inventory2Outlined />} color="#d97706" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard title="Below Safety Stock" value={belowSafetyCount} icon={<WarningAmberRounded />} color="#dc2626" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard title="Total Recommended Qty" value={totalRecommendedQty.toLocaleString('en-IN')} icon={<PlaylistAddCheck />} color="#0284c7" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard title="Vendors Involved" value={vendorCount} icon={<ShoppingCartCheckout />} color="#059669" />
            </Grid>
          </Grid>

          <FilterBar>
            <TextField
              size="small"
              placeholder="Search item or warehouse…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 260 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18 }} color="disabled" />
                  </InputAdornment>
                ),
              }}
            />
            <FilterSpacer />
            <Typography variant="body2" color="text.secondary">
              <b>{filtered.length}</b> {filtered.length === 1 ? 'item' : 'items'}
            </Typography>
          </FilterBar>

          <TableSurface>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {['Item', 'Warehouse', 'Status', 'Current Stock', 'Reorder Level', 'Safety Stock', 'Lead Time', 'Daily Consumption', 'Target Level', 'Recommended Qty', 'Preferred Vendor', 'Action'].map((h, i) => (
                      <TableCell key={h} sx={headerCellSx} align={i >= 3 && i <= 9 ? 'right' : 'left'}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} sx={{ border: 0 }}>
                        <TableEmptyState
                          title={rows.length === 0 ? 'All items are above reorder level' : 'No items match this search'}
                          hint={rows.length === 0 ? 'Nothing needs replenishment right now.' : 'Try a different search term.'}
                        />
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((r) => {
                      const belowSafety = r.safetyStock > 0 && r.currentStock <= r.safetyStock;
                      return (
                        <TableRow key={`${r.productId}-${r.warehouseId ?? 0}`} hover sx={bodyRowSx}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{r.productName}</Typography>
                            <Typography variant="caption" color="text.secondary">{r.productCode}</Typography>
                          </TableCell>
                          <TableCell>{r.warehouseName || '—'}</TableCell>
                          <TableCell>
                            <StatusDot label={belowSafety ? 'Below Safety' : 'Below Reorder'} tone={belowSafety ? 'error' : 'warning'} />
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{r.currentStock}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.reorderLevel}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.safetyStock || '—'}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.leadTimeDays != null ? `${r.leadTimeDays}d` : '—'}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.dailyConsumptionRate}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.targetLevel}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#0284c7' }}>{r.recommendedOrderQty}</TableCell>
                          <TableCell>{r.preferredVendor?.vendorName || '—'}</TableCell>
                          <TableCell>
                            <Tooltip title={r.createPrLink ? 'Create purchase requisition' : 'No PR link available'}>
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={!r.createPrLink}
                                  onClick={() => r.createPrLink && navigate(r.createPrLink)}
                                >
                                  Create PR
                                </Button>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TableSurface>
        </>
      )}
    </Box>
  );
};

export default ReorderPlanningPage;
